// Pure matchup → chart-series math for the team/league charts.
import { Matchup, Team } from '../types';

export interface XY { x: number; y: number }
export interface RaceSeries { teamId: number; label: string; points: XY[]; final: number }

const completed = (matchups: Matchup[]) => matchups.filter(mu => mu.is_completed);

/** My team's score per completed week, ascending. */
export function teamWeeklySeries(matchups: Matchup[], teamId: number): XY[] {
  return completed(matchups)
    .filter(mu => mu.team1_id === teamId || mu.team2_id === teamId)
    .map(mu => ({ x: mu.week, y: mu.team1_id === teamId ? mu.team1_score : mu.team2_score }))
    .sort((a, b) => a.x - b.x);
}

/** Median of a numeric array: middle value (odd) or mean of the middle two (even). */
export function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Median of all team scores per completed week, ascending. */
export function leagueMedianSeries(matchups: Matchup[]): XY[] {
  const byWeek = new Map<number, number[]>();
  for (const mu of completed(matchups)) {
    const scores = byWeek.get(mu.week) ?? [];
    scores.push(mu.team1_score, mu.team2_score);
    byWeek.set(mu.week, scores);
  }
  return Array.from(byWeek.entries())
    .map(([week, scores]) => ({ x: week, y: median(scores) }))
    .sort((a, b) => a.x - b.x);
}

/** Cumulative points per team across completed weeks; missing weeks carry forward. */
export function cumulativeRace(matchups: Matchup[], teams: Team[]): RaceSeries[] {
  const done = completed(matchups);
  if (done.length === 0) return [];
  const weeks = Array.from(new Set(done.map(mu => mu.week))).sort((a, b) => a - b);

  return teams.map(t => {
    let running = 0;
    const points = weeks.map(week => {
      const mu = done.find(x => x.week === week && (x.team1_id === t.id || x.team2_id === t.id));
      if (mu) running += mu.team1_id === t.id ? mu.team1_score : mu.team2_score;
      return { x: week, y: Math.round(running * 10) / 10 };
    });
    return { teamId: t.id, label: t.team_name, points, final: points[points.length - 1]?.y ?? 0 };
  });
}
